import { InteractionRequiredAuthError } from "@azure/msal-browser";


//manage automatic token refresh for iframe authentication
class TokenRefreshService {
  constructor() {
    this.refreshInterval = null;
    this.tokenExpirationTime = null;
    this.callbacks = new Set(); 
  }

  // Calculate token expiration time from JWT
  getTokenExpirationTime(idToken) {
    try {
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('Failed to parse token expiration:', error);
      return null;
    }
  }

  // Register a callback to be notified when token is refreshed
  registerCallback(callback) {
    this.callbacks.add(callback);
    return () => this.callbacks.delete(callback); 
  }

  // Notify all registered callbacks with new token
  notifyCallbacks(newToken) {
    this.callbacks.forEach(callback => {
      try {
        callback(newToken);
      } catch (error) {
        console.error('Error in token refresh callback:', error);
      }
    });
  }

  /**
   * Start automatic token refresh
   * Refreshes 10 minutes before expiration (50 minutes if token is 60 min)
   */
  startTokenRefresh(msalInstance, accounts, initialToken) {
    // Stop any existing refresh interval
    this.stopTokenRefresh();

    if (!initialToken) {
      console.warn('No initial token provided for refresh service');
      return;
    }

    // Calculate when to refresh (10 minutes before expiration)
    const expirationTime = this.getTokenExpirationTime(initialToken);
    if (!expirationTime) {
      console.warn('Could not determine token expiration time');
      return;
    }

    this.tokenExpirationTime = expirationTime;
    const currentTime = Date.now();
    const timeUntilExpiration = expirationTime - currentTime;
    const refreshTime = timeUntilExpiration - (10 * 60 * 1000); // 10 minutes before expiry

    // Schedule the refresh
    if (refreshTime > 0) {
      this.refreshInterval = setTimeout(async () => {
        try {
          const newToken = await this.refreshToken(msalInstance, accounts);
          this.notifyCallbacks(newToken);
          
          // Schedule next refresh
          this.startTokenRefresh(msalInstance, accounts, newToken);
        } catch (error) {
          console.error('Token refresh failed:', error);
          // Retry after 1 minute if refresh fails
          setTimeout(() => {
            this.startTokenRefresh(msalInstance, accounts, initialToken);
          }, 60 * 1000);
        }
      }, refreshTime);
    } else {
      // Token already expired or about to expire, refresh immediately
      this.refreshToken(msalInstance, accounts)
        .then(newToken => {
          this.notifyCallbacks(newToken);
          this.startTokenRefresh(msalInstance, accounts, newToken);
        })
        .catch(error => {
          console.error('Immediate token refresh failed:', error);
        });
    }
  }

  //Refresh the token silently
  async refreshToken(msalInstance, accounts) {
    if (!accounts || accounts.length === 0) {
      throw new Error("No MSAL accounts available for refresh");
    }

    const activeAccount = accounts[0];

    try {
      const request = {
        scopes: ["openid", "profile"],
        account: activeAccount,
        forceRefresh: true // Force a new token
      };

      const response = await msalInstance.acquireTokenSilent(request);
      return response.idToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        console.error('User interaction required for token refresh');
        throw new Error("Session expired - please log in again");
      }
      throw error;
    }
  }

  //Stop token refresh
  stopTokenRefresh() {
    if (this.refreshInterval) {
      clearTimeout(this.refreshInterval);
      this.refreshInterval = null;
    }
    this.callbacks.clear();
  }

  //Get time until token expiration
  getTimeUntilExpiration() {
    if (!this.tokenExpirationTime) return null;
    return Math.max(0, this.tokenExpirationTime - Date.now());
  }
}

export const tokenRefreshService = new TokenRefreshService();